import React from 'react'

type StyledSVGIconProps = {
	readonly svgContent: string
	readonly height: string
	readonly altColor?: string
} & React.HTMLAttributes<SVGElement>

function StyledSVGIcon(props: StyledSVGIconProps) {
	const { height, altColor, svgContent, ...rest } = props

	// Inline style for height
	const style: React.CSSProperties = {
		height,
		width: 'auto',
		display: 'inline-block',
	}

	// Parse the SVG string and render as React element
	function renderSVG(svg: string) {
		const parser = new DOMParser()
		const doc = parser.parseFromString(svg, 'image/svg+xml')
		const svgElement = doc.documentElement

		// Replace fill color if altColor is provided
		if (altColor) {
			const allElements = svgElement.querySelectorAll('[fill]')
			for (const el of allElements) {
				el.setAttribute('fill', altColor)
			}
		}

		// Helper to recursively convert SVG DOM nodes to React elements
		function domNodeToReact(node: Element): React.ReactNode {
			const children = Array.from(node.childNodes)
				.filter(child => child.nodeType === 1)
				.map(child => domNodeToReact(child as Element))

			const props: Record<string, string> = {}
			for (const attr of Array.from(node.attributes)) {
				props[attr.name === 'class' ? 'className' : attr.name] = attr.value
			}

			return React.createElement(node.tagName.toLowerCase(), props, children.length ? children : undefined)
		}

		// Render the SVG element and its children
		return (
			<svg
				style={style}
				className='transition-all duration-150'
				width={svgElement.getAttribute('width') || 'auto'}
				viewBox={svgElement.getAttribute('viewBox') || undefined}
				{...rest}
				xmlns='http://www.w3.org/2000/svg'>
				<title>{rest['aria-label'] || 'SVG Icon'}</title>
				{Array.from(svgElement.childNodes)
					.filter(child => child.nodeType === 1)
					.map((child, idx) => {
						const element = domNodeToReact(child as Element)
						const childElement = child as Element
						const key = `${childElement.tagName}-${idx}-${childElement.getAttribute('id') || childElement.getAttribute('class') || ''}`
						return React.isValidElement(element) ? React.cloneElement(element, { key }) : element
					})}
			</svg>
		)
	}

	return renderSVG(svgContent)
}

export default StyledSVGIcon
